import { opacityCode } from './constants/opacity';

/** `hex` (#rrggbb) at this opacity, from 0 to 1, as #rrggbbaa. */
export const withAlpha = (hex: string, opacity: number) =>
  `${hex}${Math.round(Math.min(Math.max(opacity, 0), 1) * 255)
    .toString(16)
    .padStart(2, '0')}`;

type Rgb = [number, number, number];

// #rgb, #rrggbb (any alpha after them ignored) or rgb()/rgba(), as FullCalendar and the themes write colours.
const toRgb = (color?: string | null): Rgb | null => {
  if (!color) return null;
  const hex = color.trim().match(/^#([0-9a-f]{3}|[0-9a-f]{6})(?:[0-9a-f]{2})?$/i);
  if (hex) {
    const digits = hex[1].length === 3 ? hex[1].replace(/(.)/g, '$1$1') : hex[1];
    return [0, 2, 4].map(at => parseInt(digits.slice(at, at + 2), 16)) as Rgb;
  }
  const rgb = color.match(/rgba?\(\s*(\d+)[\s,]+(\d+)[\s,]+(\d+)/i);
  return rgb ? [Number(rgb[1]), Number(rgb[2]), Number(rgb[3])] : null;
};

const luminance = ([r, g, b]: Rgb) => {
  const channel = (value: number) => {
    const c = value / 255;
    return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  };
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
};

const contrast = (a: Rgb, b: Rgb) => {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x);
  return (light + 0.05) / (dark + 0.05);
};

/**
 * Text in `color` that reads on `background`: the colour itself when it already does, else mixed toward white on a
 * dark background (toward black on a light one) just far enough, so it keeps its hue. Made for course colours on dark
 * calendar blocks (Petr, 2026-10-08), which in their own saturated colour were hard to read.
 */
export const readableTextColor = (color: string, background: string, minContrast = 4.5) => {
  const text = toRgb(color);
  const back = toRgb(background);
  if (!text || !back || contrast(text, back) >= minContrast) return color;
  const target: Rgb = luminance(back) < 0.5 ? [255, 255, 255] : [0, 0, 0];
  for (let step = 1; step <= 10; step++) {
    const mixed = text.map((value, i) => Math.round(value + (target[i] - value) * (step / 10))) as Rgb;
    if (contrast(mixed, back) >= minContrast) return `rgb(${mixed.join(', ')})`;
  }
  return `rgb(${target.join(', ')})`;
};

/** `a` moved `weight` (0 to 1) of the way toward `b`, as #rrggbb; `a` as it was when either isn't a colour read here. */
export const mixColors = (a: string, b: string, weight: number) => {
  const from = toRgb(a);
  const to = toRgb(b);
  if (!from || !to) return a;
  return `#${from
    .map((value, i) =>
      Math.round(value + (to[i] - value) * weight)
        .toString(16)
        .padStart(2, '0')
    )
    .join('')}`;
};

export const increaseColorBrightness = (hex: string, percent: number = 80) => {
  // strip the leading # if it's there
  hex = hex.replace(/^\s*#|\s*$/g, '');

  // convert 3 char codes --> 6, e.g. `E0F` --> `EE00FF`
  if (hex.length === 3) {
    hex = hex.replace(/(.)/g, '$1$1');
  }

  const r = parseInt(hex.substr(0, 2), 16),
    g = parseInt(hex.substr(2, 2), 16),
    b = parseInt(hex.substr(4, 2), 16);

  // return `rgba(${r}, ${g}, ${b}, 0.3)`;

  return (
    '#' +
    (0 | ((1 << 8) + r + ((256 - r) * percent) / 100)).toString(16).substr(1) +
    (0 | ((1 << 8) + g + ((256 - g) * percent) / 100)).toString(16).substr(1) +
    (0 | ((1 << 8) + b + ((256 - b) * percent) / 100)).toString(16).substr(1)
  );
};

export const darkenColor = (color: string, percent: number = -60) => {
  let R = parseInt(color.substring(1, 3), 16);
  let G = parseInt(color.substring(3, 5), 16);
  let B = parseInt(color.substring(5, 7), 16);

  R = Math.floor((R * (100 + percent)) / 100);
  G = Math.floor((G * (100 + percent)) / 100);
  B = Math.floor((B * (100 + percent)) / 100);

  R = R < 255 ? R : 255;
  G = G < 255 ? G : 255;
  B = B < 255 ? B : 255;

  const RR = R.toString(16).length == 1 ? '0' + R.toString(16) : R.toString(16);
  const GG = G.toString(16).length == 1 ? '0' + G.toString(16) : G.toString(16);
  const BB = B.toString(16).length == 1 ? '0' + B.toString(16) : B.toString(16);

  return '#' + RR + GG + BB;
};

export const getOpacityCode = (opacity: string) => {
  return opacityCode.get(opacity);
};

export const pickerColors = [
  '#F47373',
  '#F06292',
  '#F06DE7',
  '#9575CD',
  '#7986CB',
  '#9ABFE7',
  '#6ECBF4',
  '#54D1E1',
  '#4DB6AC',
  '#81C784',
  '#AED581',
  '#DCE775',
  '#FFD54F',
  '#FFB74D',
  '#FF8A65',
  '#D27979',
  '#A1887F',
  '#90A4AE',

  '#F44336',
  '#E91E63',
  '#F32EE5',
  '#9052FF',
  '#5E76FF',
  '#5E8FFF',
  '#2FB8F7',
  '#00C6DF',
  '#02CCB9',
  '#00C708',
  '#76DB00',
  '#CDDE21',
  '#FFC107',
  '#FF9800',
  '#FF5722',
  '#FF5A5A',
  '#795548',
  '#607D8B',

  '#D32F2F',
  '#C2185B',
  '#B54BAD',
  '#512DA8',
  '#303F9F',
  '#3976FF',
  '#0288D1',
  '#0097A7',
  '#00796B',
  '#388E3C',
  '#689F38',
  '#AFB42B',
  '#FFA000',
  '#F57C00',
  '#E64A19',
  '#BF4040',
  '#5D4037',
  '#455A64'
];

export const editorTextColorPalette = ['#3F4E61', '#798799', '#C4CDD8', '#E4E8EC', '#F4F5F6', '#FFFFFF'];

export const editorBgColorPalette = ['#20272D', '#798799', '#C4CDD8', '#E4E8EC', '#F4F5F6', '#FFFFFF'];

export const getRandomPickerColor = () => pickerColors[Math.floor(Math.random() * pickerColors.length)];
